import type { ActivePackage } from '@/types/package';

export const PACKAGE_BADGE_IDS = {
  mostPopular: 'cme11acpr0001jjcfsi07xv12',
  bestValue: 'cmnkph433000aaqs3j526q3pf',
} as const;

export type PlanTheme = {
  titleClass: string;
  pillClass: string;
  priceClass: string;
  iconBgClass: string;
  iconClass: string;
  buttonClass: string;
  borderClass: string;
};

export const PAID_PLAN_THEME: PlanTheme = {
  titleClass: 'text-gray-900',
  pillClass: 'bg-primary-50 text-primary-700',
  priceClass: 'text-gray-900',
  iconBgClass: 'bg-primary-100',
  iconClass: 'text-primary-600',
  buttonClass: 'bg-primary-600 hover:bg-primary-700',
  borderClass: 'border-primary-200',
};

export function getPaidPlanTheme(): PlanTheme {
  return PAID_PLAN_THEME;
}

export function formatInrPrice(price: number): string {
  return `₹${Math.floor(price).toLocaleString('en-IN')}`;
}

export function getPackageSavings(pkg: ActivePackage): {
  amount: number;
  percent: number;
} | null {
  if (pkg.price <= pkg.offerPrice) {
    return null;
  }

  const amount = pkg.price - pkg.offerPrice;
  const percent = Math.round((amount / pkg.price) * 100);
  return { amount, percent };
}

export function getPackageBadge(pkg: ActivePackage): {
  label: string;
  className: string;
} | null {
  if (pkg.id === PACKAGE_BADGE_IDS.mostPopular) {
    return {
      label: 'MOST POPULAR',
      className: 'bg-gradient-to-r from-purple-500 to-purple-600 text-white',
    };
  }

  if (pkg.id === PACKAGE_BADGE_IDS.bestValue) {
    return {
      label: 'BEST VALUE',
      className: 'bg-gradient-to-r from-yellow-400 to-orange-400 text-yellow-900',
    };
  }

  return null;
}

export type CardFeature = {
  name: string;
  included: boolean;
  value?: string;
};

export function buildFreeFeatures(): CardFeature[] {
  return BASIC_INCLUDED_FEATURES.map((name) => ({
    name,
    included: true,
  }));
}

export function buildCardFeatures(pkg: ActivePackage): CardFeature[] {
  return [
    {
      name: 'View Contacts',
      included: pkg.mobileNumberAccessCount > 0,
      value: String(pkg.mobileNumberAccessCount),
    },
    {
      name: 'Send Messages',
      included: pkg.sendMessageCount > 0,
      value: String(pkg.sendMessageCount),
    },
    {
      name: 'Member Chat',
      included: pkg.memberChat,
      value: pkg.memberChat ? String(pkg.chatUsersCount) : undefined,
    },
    {
      name: 'Horoscope + Kundali Matching',
      included: pkg.viewHoroscope,
      value: pkg.viewHoroscope ? String(pkg.horoscopeCount) : undefined,
    },
    {
      name: 'Audio Calling',
      included: pkg.isAudioCalling,
      value: pkg.isAudioCalling ? `${pkg.audioCallingMins} mins` : undefined,
    },
    {
      name: 'Video Calling',
      included: pkg.isVideoCalling,
      value: pkg.isVideoCalling ? `${pkg.videoCallingMins} mins` : undefined,
    },
  ];
}

export function buildFullCardFeatures(pkg: ActivePackage): CardFeature[] {
  return [...buildFreeFeatures(), ...buildCardFeatures(pkg)];
}

export type ComparisonFeatureRow = {
  label: string;
  freeIncluded: boolean;
  getIncluded: (pkg: ActivePackage) => boolean;
  getDisplayValue?: (pkg: ActivePackage) => string | null;
};

export const BASIC_INCLUDED_FEATURES = [
  'Send Interest',
  'Shortlist Profile',
  'Like Profile',
  'View Profile',
] as const;

export const COMPARISON_FEATURE_ROWS: ComparisonFeatureRow[] = [
  {
    label: 'View Contacts',
    freeIncluded: false,
    getIncluded: (pkg) => pkg.mobileNumberAccessCount > 0,
    getDisplayValue: (pkg) =>
      pkg.mobileNumberAccessCount > 0 ? String(pkg.mobileNumberAccessCount) : null,
  },
  {
    label: 'Send Messages',
    freeIncluded: false,
    getIncluded: (pkg) => pkg.sendMessageCount > 0,
    getDisplayValue: (pkg) => (pkg.sendMessageCount > 0 ? String(pkg.sendMessageCount) : null),
  },
  {
    label: 'Chat',
    freeIncluded: false,
    getIncluded: (pkg) => pkg.memberChat,
    getDisplayValue: (pkg) => (pkg.memberChat ? String(pkg.chatUsersCount) : null),
  },
  {
    label: 'View Horoscope',
    freeIncluded: false,
    getIncluded: (pkg) => pkg.viewHoroscope,
    getDisplayValue: (pkg) => (pkg.viewHoroscope ? String(pkg.horoscopeCount) : null),
  },
  {
    label: 'Kundali Matching',
    freeIncluded: false,
    getIncluded: (pkg) => pkg.viewHoroscope,
    getDisplayValue: (pkg) => (pkg.viewHoroscope ? String(pkg.horoscopeCount) : null),
  },
  {
    label: 'Audio Calling',
    freeIncluded: false,
    getIncluded: (pkg) => pkg.isAudioCalling,
    getDisplayValue: (pkg) =>
      pkg.isAudioCalling ? `${pkg.audioCallingMins} mins` : null,
  },
  {
    label: 'Video Calling',
    freeIncluded: false,
    getIncluded: (pkg) => pkg.isVideoCalling,
    getDisplayValue: (pkg) =>
      pkg.isVideoCalling ? `${pkg.videoCallingMins} mins` : null,
  },
  {
    label: 'Quick Chat Support',
    freeIncluded: false,
    getIncluded: (pkg) => pkg.instantQuickCallSupport,
  },
  {
    label: 'Personal Relationship Manager',
    freeIncluded: false,
    getIncluded: (pkg) => pkg.personalRelationshipManager,
  },
  {
    label: 'Expert Profile Writing',
    freeIncluded: false,
    getIncluded: (pkg) => pkg.expertProfileWritingService,
  },
  {
    label: 'Assisted Dashboard',
    freeIncluded: false,
    getIncluded: (pkg) => pkg.assistedDashboard,
  },
];

export function getOneYearPackage(packages: ActivePackage[]): ActivePackage | null {
  return packages.find((pkg) => pkg.durationMonths === 12) ?? null;
}

export function buildMembershipFaqAnswer(
  template: string,
  oneYearPackage: ActivePackage | null
): string {
  if (!oneYearPackage) {
    return template;
  }

  const priceText = formatInrPrice(oneYearPackage.offerPrice);
  return template.replace(/Rs\s*2000\/-|\₹2,000/gi, priceText);
}
